import type { FC, ReactNode } from 'react'

import { useState } from 'react'

import type { Command, GalleryItem } from '@/components/landing/console/data'

import { GALLERY, WORK } from '@/components/landing/console/data'

interface Win9Props {
    title: string
    footer?: string
    onClose: () => void
    children: ReactNode
}

const Win9: FC<Win9Props> = ({ title, footer = 'esc / ✕ to close', onClose, children }): ReactNode => {
    return (
        <div className='win9'>
            <div className='win9-bar'>
                <button type='button' className='win9-x' aria-label='Close' onClick={onClose}>
                    ×
                </button>

                <span className='win9-title'>
                    {title}
                </span>
            </div>

            <div className='win9-body'>
                {children}
            </div>

            <div className='win9-foot'>
                {footer}
            </div>
        </div>
    )
}

interface GalleryWindowProps {
    index: number
    onPrev: () => void
    onNext: () => void
    onClose: () => void
}

const GalleryWindow: FC<GalleryWindowProps> = ({ index, onPrev, onNext, onClose }): ReactNode => {
    const items: GalleryItem[] = GALLERY
    const item = items[index] ?? items[0]

    return (
        <Win9
            title={`Gallery — ${index + 1}/${items.length}`}
            footer='◄ ► or arrow keys to browse'
            onClose={onClose}
        >
            <div className='galv'>
                <div className='galv-frame' style={{ background: item.pattern }} aria-hidden='true' />

                <div className='galv-name'>
                    {item.name}
                </div>

                <div className='galv-desc'>
                    {item.desc}
                </div>

                <div className='galv-nav'>
                    <button type='button' className='galv-arrow' aria-label='Previous' onClick={onPrev}>
                        ◄
                    </button>

                    <div className='galv-dots'>
                        {items.map((it, i) => (
                            <span key={it.name} className={i === index ? 'on' : undefined}>
                                ●
                            </span>
                        ))}
                    </div>

                    <button type='button' className='galv-arrow' aria-label='Next' onClick={onNext}>
                        ►
                    </button>
                </div>
            </div>
        </Win9>
    )
}

const PricingWindow: FC<{ onClose: () => void }> = ({ onClose }): ReactNode => {
    return (
        <Win9 title='Motion Pricing' onClose={onClose}>
            <div className='pr'>
                <div className='pr-row'>
                    <span className='pr-name'>Monthly</span>
                    <span className='pr-dots' />
                    <span className='pr-amt'>$9/mo</span>
                </div>
                <p className='pr-feat'>every section · cancel anytime</p>

                <div className='pr-row'>
                    <span className='pr-name'>Lifetime</span>
                    <span className='pr-dots' />
                    <span className='pr-amt'>$99</span>
                    <span className='pr-best'>★ best</span>
                </div>
                <p className='pr-feat'>every section + all future · forever</p>

                <div className='pr-btns'>
                    <button type='button' className='pr-btn'>
                        [ Subscribe ]
                    </button>
                    <button type='button' className='pr-btn pr-btn-on'>
                        [ Buy Lifetime ]
                    </button>
                </div>
            </div>
        </Win9>
    )
}

const AboutWindow: FC<{ onClose: () => void }> = ({ onClose }): ReactNode => {
    return (
        <Win9 title='About Motion' footer='readme.txt' onClose={onClose}>
            <div className='ab'>
                <p>
                    Motion is a collection of interactive web experiments, tools and
                    physics-based scenes you can drop into any site.
                </p>
                <p>
                    Subscribe once and get the prompt and spec for every section in the
                    gallery — plus every one shipped later. No per-section purchases.
                </p>

                <div className='ab-meta'>
                    <span>26+ sections</span>
                    <span>2 plans</span>
                    <span>∞ future</span>
                </div>

                <div className='ab-sig'>
                    — made to make people stop scrolling.
                </div>
            </div>
        </Win9>
    )
}

type ContactStatus = 'idle' | 'sending' | 'sent' | 'error'

const ContactWindow: FC<{ onClose: () => void }> = ({ onClose }): ReactNode => {
    const [email, setEmail] = useState('')
    const [message, setMessage] = useState('')
    const [website, setWebsite] = useState('')
    const [status, setStatus] = useState<ContactStatus>('idle')

    const canSend = email.trim() !== '' && message.trim() !== '' && status !== 'sending'

    const note =
        status === 'sent'
            ? 'sent — talk soon.'
            : status === 'error'
                ? 'send failed — try again.'
                : 'replies within 24h · hello@yourdomain.com'

    const submit = async (): Promise<void> => {
        if (!canSend) return
        setStatus('sending')

        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    from: email.trim(),
                    subject: `message from ${email.trim()}`,
                    body: message.trim(),
                    website
                })
            })

            if (!res.ok) throw new Error('failed')

            setStatus('sent')
            setEmail('')
            setMessage('')
        } catch {
            setStatus('error')
        }
    }

    return (
        <Win9 title='Contact' onClose={onClose}>
            <form
                className='ct'
                onSubmit={(e) => {
                    e.preventDefault()
                    void submit()
                }}
            >
                <div className='ct-row'>
                    <label className='ct-lbl' htmlFor='ct-email'>email</label>
                    <input
                        id='ct-email'
                        className='ct-in'
                        type='email'
                        autoComplete='email'
                        placeholder='you@email.com'
                        aria-label='Email'
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>

                <div className='ct-row ct-col'>
                    <label className='ct-lbl' htmlFor='ct-msg'>message</label>
                    <textarea
                        id='ct-msg'
                        className='ct-in ct-area'
                        rows={3}
                        placeholder='say hi...'
                        aria-label='Message'
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                    />
                </div>

                {/* Honeypot — hidden from humans, your endpoint drops filled ones. */}
                <input
                    className='ct-honey'
                    type='text'
                    name='website'
                    tabIndex={-1}
                    autoComplete='off'
                    aria-hidden='true'
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                />

                <div className='ct-actions'>
                    <button type='submit' className='ct-send' disabled={!canSend}>
                        {status === 'sending' ? '[ Sending... ]' : '[ Send ]'}
                    </button>

                    <span className='ct-note' data-status={status}>
                        {note}
                    </span>
                </div>
            </form>
        </Win9>
    )
}

const WorkWindow: FC<{ onClose: () => void }> = ({ onClose }): ReactNode => {
    return (
        <Win9 title='Recent.log' footer={`${WORK.length} entries`} onClose={onClose}>
            <div className='wk'>
                {WORK.map((row) => (
                    <div className='wk-row' key={row.name}>
                        <span className='wk-name'>{row.name}</span>
                        <span className='wk-dots' />
                        <span className='wk-tag'>{row.tag}</span>
                        <span className='wk-date'>{row.date}</span>
                    </div>
                ))}
            </div>
        </Win9>
    )
}

interface AppWindowProps {
    command: Command
    galleryIndex: number
    onGalleryPrev: () => void
    onGalleryNext: () => void
    onClose: () => void
}

export const AppWindow: FC<AppWindowProps> = ({
    command,
    galleryIndex,
    onGalleryPrev,
    onGalleryNext,
    onClose
}): ReactNode => {
    return (
        <div className='app'>
            {command === 'gallery' && (
                <GalleryWindow
                    index={galleryIndex}
                    onPrev={onGalleryPrev}
                    onNext={onGalleryNext}
                    onClose={onClose}
                />
            )}
            {command === 'pricing' && <PricingWindow onClose={onClose} />}
            {command === 'about' && <AboutWindow onClose={onClose} />}
            {command === 'contact' && <ContactWindow onClose={onClose} />}
            {command === 'work' && <WorkWindow onClose={onClose} />}
        </div>
    )
}
